import type { IconProps } from '@shared/icons/types';
import { twMerge } from '@shared/themes';
import Image from 'next/image';
import React from 'react';
import { IconBadge } from '../IconBadge/IconBadge';

type BaseContentPodProps = Omit<React.ComponentProps<'div'>, 'title'> & {
  title: string;
  description: string;
};

type IconVariantProps = BaseContentPodProps & {
  variant?: 'icon';
  icon: React.ComponentType<IconProps>;
  imageUrl?: never;
  imageAlt?: never;
};

type ImageVariantProps = BaseContentPodProps & {
  variant: 'image';
  imageUrl: string;
  imageAlt: string;
  icon?: never;
};

type ContentPodProps = IconVariantProps | ImageVariantProps;

function ContentPod(props: ContentPodProps) {
  if (props.variant === 'image') {
    const { imageUrl, imageAlt, title, description, className = 'my-4', ...restProps } = props;
    return (
      <div data-testid="content-pod" className={twMerge('w-full border border-border rounded-lg overflow-hidden md:mx-auto md:h-79', className)} {...restProps}>
        <div className="flex flex-col md:flex-row items-stretch w-full md:h-79">
          <div className={twMerge('w-full md:w-1/2 overflow-hidden relative h-49 md:h-full')}>
            <Image src={imageUrl} alt={imageAlt} fill sizes="(max-width: 768px) 100vw, 50vw" className="object-cover" priority={false} loading="lazy" />
          </div>

          <div className="w-full md:w-1/2 bg-container-base flex items-center p-5 md:p-16 md:h-full">
            <div className="w-full">
              <div className="flex flex-col gap-2">
                <h3 className="heading-5">{title}</h3>
                <p className="text-foreground-subtle paragraph-explanatory">{description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { icon, title, description, className, ...restProps } = props;
  return (
    <div data-testid="content-pod" className={twMerge('h-full', className)} {...restProps}>
      <div className="flex flex-col h-full bg-container-base border border-border rounded-lg p-5">
        <div className="flex flex-col">
          <IconBadge icon={icon} />
          <h3 className="mt-4 text-foreground">{title}</h3>
          <p className="text-foreground-subtle paragraph-explanatory mt-2">{description}</p>
        </div>
      </div>
    </div>
  );
}

export { ContentPod };
export type { ContentPodProps };
